/** The time granularity the AI Visibility trends are bucketed by. */
export type VisibilityDisplayMode = 'day' | 'week' | 'month';

/**
 * Under this many days old, a project's trends default to DAYS.
 *
 * A brand-new project has a handful of runs; bucketing them by month draws a single point and the
 * page looks broken rather than new.
 */
export const DAILY_DEFAULT_MAX_AGE_DAYS = 14;

/**
 * Under this many days old, a project's trends default to WEEKS.
 *
 * "Three months" expressed in days. Calendar months would make the boundary drift by a day or two
 * depending on the month, and this is a coarse opening view — nobody can see the difference between
 * 90 and 92 days of bucketing, but a boundary that moves is harder to test and explain.
 */
export const WEEKLY_DEFAULT_MAX_AGE_DAYS = 90;

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * The granularity a project should OPEN on, from how long it has been collecting data.
 *
 * A young project has too few buckets to say anything at a coarse granularity; an old one has too
 * many to read at a fine one. This picks the one that shows a usable shape on arrival.
 *
 * Boundaries are exclusive on the lower side: exactly 14 days old is already past "under 14 days",
 * so it moves to weeks, and exactly 90 days moves to months. A project with no usable creation date
 * falls back to months, the safest default for an unknown amount of history.
 */
export function defaultVisibilityDisplayMode(
  projectCreatedAt: Date | string | number | undefined,
  now: Date = new Date()
): VisibilityDisplayMode {
  const createdAt = toTimestamp(projectCreatedAt);
  if (createdAt === undefined) {
    return 'month';
  }

  const ageDays = (now.getTime() - createdAt) / MILLISECONDS_PER_DAY;
  // A creation date in the future is not a young project, it is a bad date; treat it as unknown.
  if (!Number.isFinite(ageDays) || ageDays < 0) {
    return 'month';
  }

  if (ageDays < DAILY_DEFAULT_MAX_AGE_DAYS) {
    return 'day';
  }
  if (ageDays < WEEKLY_DEFAULT_MAX_AGE_DAYS) {
    return 'week';
  }
  return 'month';
}

/**
 * How many buckets the trends ask for at the coarse granularities.
 *
 * Six weeks or six months is a readable span at a glance; more crowds the axis without adding a
 * shape anyone reads.
 */
export const COARSE_HISTORY_BUCKETS = 6;

/**
 * How many DAILY buckets the trends ask for: the last fourteen days plus today.
 *
 * Today is deliberately included and deliberately partial — a reader checking this morning's
 * answers expects to find them, and a window ending yesterday made the freshest data the one thing
 * the chart would not show. Fourteen days behind it is two full weeks, enough to read a direction
 * without the crowding a month of daily points produced.
 */
export const DAILY_HISTORY_BUCKETS = 15;

/** Days of history the daily window covers BEHIND today. */
export const DAILY_HISTORY_TRAILING_DAYS = DAILY_HISTORY_BUCKETS - 1;

/** How many buckets to request for a granularity. */
export function historyBucketCount(mode: VisibilityDisplayMode): number {
  return mode === 'day' ? DAILY_HISTORY_BUCKETS : COARSE_HISTORY_BUCKETS;
}

/** Every value the URL parameter may carry, so an unknown one can be rejected rather than trusted. */
const DISPLAY_MODES: readonly VisibilityDisplayMode[] = ['day', 'week', 'month'];

/** The granularity named by the URL, or undefined when it names nothing usable. */
export function parseVisibilityDisplayMode(value: string | null | undefined): VisibilityDisplayMode | undefined {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase();
  return (DISPLAY_MODES as readonly string[]).includes(normalized) ? (normalized as VisibilityDisplayMode) : undefined;
}

/**
 * The granularity to show: what the reader explicitly chose, else the age-derived default.
 *
 * An explicit choice always wins — including when it happens to equal the default — because the
 * point of the parameter is that the reader decided. The default only fills a gap.
 */
export function resolveVisibilityDisplayMode(
  requested: string | null | undefined,
  projectCreatedAt: Date | string | number | undefined,
  now: Date = new Date()
): VisibilityDisplayMode {
  return parseVisibilityDisplayMode(requested) ?? defaultVisibilityDisplayMode(projectCreatedAt, now);
}

function toTimestamp(value: Date | string | number | undefined): number | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  const parsed = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : undefined;
}
